// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { useState, useEffect } from 'react';
import * as styles from '../styles/AssessmentInitiation.styles';
import { WorkflowFlowchart } from './WorkflowFlowchart';
import { LevelsInfoPopup } from './LevelsInfoPopup';

const ArrowRightIcon = (): JSX.Element => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

export interface AssessmentInitiationProps {
  onStartAssessment: () => void;
  functionalArea: string;
  setFunctionalArea: (value: string) => void;
  expertiseLevel: string;
  setExpertiseLevel: (value: string) => void;
  dimensionToBeAssessed?: string;
  setDimensionToBeAssessed?: (value: string) => void;
  userEmail?: string;
  userName?: string;
}

export const AssessmentInitiation = ({
  onStartAssessment,
  functionalArea,
  setFunctionalArea,
  expertiseLevel,
  setExpertiseLevel,
  userEmail,
  userName
}: AssessmentInitiationProps): JSX.Element => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showLevelsInfo, setShowLevelsInfo] = useState(false);

  const handleLevelChange = (value: string): void => {
    setExpertiseLevel(value);
    if (['1', '2', '3', '4', '5'].includes(value)) {
      setShowLevelsInfo(true);
    }
  };

  // Clear validation error when user selects a valid level
  useEffect(() => {
    if (expertiseLevel && ['1', '2', '3', '4', '5'].includes(expertiseLevel)) {
      setError(null);
    }
  }, [expertiseLevel]);

  const requiredLevelError = 'Please select Self Assessed AI Skill Level before starting the assessment.';
  const isRequiredFieldError = error === requiredLevelError;

  const handleStartAssessment = async (): Promise<void> => {
    if (!expertiseLevel || !['1', '2', '3', '4', '5'].includes(expertiseLevel)) {
      setError(requiredLevelError);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const selfAssessedLevel = parseInt(expertiseLevel, 10);
      console.log('[AssessmentInitiation] Starting assessment with:', {
        functionalArea,
        selfAssessedLevel
      });

      onStartAssessment();
    } catch (err: any) {
      console.error('[AssessmentInitiation] Error:', err);
      setError(err.message || 'Failed to start assessment. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="assessment-initiation" style={styles.cardRoot}>
      <WorkflowFlowchart />
      <div style={styles.mainContentWrapper}>
        <p className="assessment-initiation-subtitle" style={styles.subtitle}>
          Please start your skill assessment by filling the details below (and use a quiet room or microphone)
        </p>

        <div style={styles.formRow}>
          <div style={{ ...styles.formGroup, ...styles.formGroupInRow, marginBottom: 0 }}>
            <label style={styles.label} htmlFor="functional-area">
              Skill to be Assessed
            </label>
            <input
              id="functional-area"
              type="text"
              style={styles.input}
              value={functionalArea}
              onChange={(e) => setFunctionalArea(e.target.value)}
              disabled={isLoading}
              placeholder="e.g., Software Engineer, Data Analyst"
            />
          </div>
          <div style={{ ...styles.formGroup, ...styles.formGroupInRow, marginBottom: 0 }}>
            <label style={styles.label} htmlFor="self-assessed-level">
              Self Assessed AI Skill Level
            </label>
            <div style={styles.selectFieldWrapper}>
              <select
                id="self-assessed-level"
                style={{
                  ...styles.select,
                  ...(isRequiredFieldError ? styles.selectError : {})
                }}
                value={expertiseLevel}
                onChange={(e) => handleLevelChange(e.target.value)}
                disabled={isLoading}
                aria-required="true"
                aria-invalid={!!isRequiredFieldError}
                aria-describedby={isRequiredFieldError ? 'skill-level-error' : undefined}
              >
                <option value="">Select your level</option>
                <option value="1">Level 1 - AI Aware (Beginner)</option>
                <option value="2">Level 2 - AI Literate (Basic Knowledge)</option>
                <option value="3">Level 3 - AI Practitioner (Active User)</option>
                <option value="4">Level 4 - AI Fluent (System Builder)</option>
                <option value="5">Level 5 - AI Expert</option>
              </select>
              {isRequiredFieldError && (
                <div id="skill-level-error" role="alert" style={styles.fieldErrorPopover}>
                  Please fill out this field
                </div>
              )}
            </div>
          </div>
          <div style={{ ...styles.formGroup, ...styles.formGroupInRow, marginBottom: 0, maxWidth: 250 }}>
            <label style={styles.label} htmlFor="self-assessed-level">
              &nbsp;
            </label>
            <button
              className="assessment-initiation-start-btn"
              style={styles.startButton}
              type="button"
              onClick={handleStartAssessment}
              disabled={isLoading}
            >
              {isLoading ? 'Initializing...' : 'Start Assessment'}
              {!isLoading && <ArrowRightIcon />}
            </button>
          </div>
        </div>

        <div style={styles.startButtonRow}></div>

        <p style={styles.disclaimer}>
          The agent detects and reports copied responses and plagiarism. This assessment is for constructive feedback
          and helping you improve your AI skills. Sovereign version coming soon.
        </p>
        {error && !isRequiredFieldError && (
          <div
            role="alert"
            style={{
              color: '#d32f2f',
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: '#ffebee',
              borderRadius: '4px'
            }}
          >
            {error}
          </div>
        )}

        {showLevelsInfo && <LevelsInfoPopup onClose={() => setShowLevelsInfo(false)} />}
      </div>
    </div>
  );
};
